/**
 * Support Ticket Service
 * Handles all ticket-related operations with Supabase
 */

import { supabase } from '../lib/supabase';
import { 
  triggerTicketCreatedNotification, 
  triggerTicketStatusUpdateNotification, 
  triggerTicketMessageNotification 
} from './notificationTriggers';

export const supportTicketService = {
  /**
   * Create a new support ticket
   */
  async createTicket(ticketData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('support_tickets')?.insert({
          user_id: user?.id,
          subject: ticketData?.subject,
          description: ticketData?.description,
          category: ticketData?.category || 'general',
          priority: ticketData?.priority || 'medium'
        })?.select(`
          *,
          user_profiles!support_tickets_user_id_fkey (
            id,
            full_name,
            email,
            avatar_url
          )
        `)?.single();

      if (error) throw error;

      // Trigger notification
      await triggerTicketCreatedNotification(data?.id, {
        subject: data?.subject,
        userId: user?.id
      });

      return {
        id: data?.id,
        userId: data?.user_id,
        subject: data?.subject,
        description: data?.description,
        category: data?.category,
        priority: data?.priority,
        status: data?.status,
        assignedTo: data?.assigned_to,
        assignedAt: data?.assigned_at,
        resolvedAt: data?.resolved_at,
        closedAt: data?.closed_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        user: data?.user_profiles ? {
          id: data?.user_profiles?.id,
          fullName: data?.user_profiles?.full_name,
          email: data?.user_profiles?.email,
          avatarUrl: data?.user_profiles?.avatar_url
        } : null
      };
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  },

  /**
   * Get all tickets for current user
   */
  async getUserTickets(filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase?.from('support_tickets')?.select(`
          *,
          user_profiles!support_tickets_user_id_fkey (
            id,
            full_name,
            email,
            avatar_url
          ),
          assigned_user:user_profiles!support_tickets_assigned_to_fkey (
            id,
            full_name,
            email
          )
        `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(ticket => ({
        id: ticket?.id,
        userId: ticket?.user_id,
        subject: ticket?.subject,
        description: ticket?.description,
        category: ticket?.category,
        priority: ticket?.priority,
        status: ticket?.status,
        assignedTo: ticket?.assigned_to,
        assignedAt: ticket?.assigned_at,
        resolvedAt: ticket?.resolved_at,
        closedAt: ticket?.closed_at,
        createdAt: ticket?.created_at,
        updatedAt: ticket?.updated_at,
        user: ticket?.user_profiles ? {
          id: ticket?.user_profiles?.id,
          fullName: ticket?.user_profiles?.full_name,
          email: ticket?.user_profiles?.email,
          avatarUrl: ticket?.user_profiles?.avatar_url
        } : null,
        assignedUser: ticket?.assigned_user ? {
          id: ticket?.assigned_user?.id,
          fullName: ticket?.assigned_user?.full_name,
          email: ticket?.assigned_user?.email
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching user tickets:', error);
      throw error;
    }
  },

  /**
   * Get all tickets (admin only)
   */
  async getAllTickets(filters = {}) {
    try {
      let query = supabase?.from('support_tickets')?.select(`
          *,
          user_profiles!support_tickets_user_id_fkey (
            id,
            full_name,
            email,
            avatar_url
          ),
          assigned_user:user_profiles!support_tickets_assigned_to_fkey (
            id,
            full_name,
            email
          )
        `)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      if (filters?.assignedTo) {
        query = query?.eq('assigned_to', filters?.assignedTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(ticket => ({
        id: ticket?.id,
        userId: ticket?.user_id,
        subject: ticket?.subject,
        description: ticket?.description,
        category: ticket?.category,
        priority: ticket?.priority,
        status: ticket?.status,
        assignedTo: ticket?.assigned_to,
        assignedAt: ticket?.assigned_at,
        resolvedAt: ticket?.resolved_at,
        closedAt: ticket?.closed_at,
        createdAt: ticket?.created_at,
        updatedAt: ticket?.updated_at,
        user: ticket?.user_profiles ? {
          id: ticket?.user_profiles?.id,
          fullName: ticket?.user_profiles?.full_name,
          email: ticket?.user_profiles?.email,
          avatarUrl: ticket?.user_profiles?.avatar_url
        } : null,
        assignedUser: ticket?.assigned_user ? {
          id: ticket?.assigned_user?.id,
          fullName: ticket?.assigned_user?.full_name,
          email: ticket?.assigned_user?.email
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching all tickets:', error);
      throw error;
    }
  },

  /**
   * Get single ticket by ID
   */
  async getTicketById(ticketId) {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.select(`
          *,
          user_profiles!support_tickets_user_id_fkey (
            id,
            full_name,
            email,
            avatar_url
          ),
          assigned_user:user_profiles!support_tickets_assigned_to_fkey (
            id,
            full_name,
            email
          )
        `)?.eq('id', ticketId)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        subject: data?.subject,
        description: data?.description,
        category: data?.category,
        priority: data?.priority,
        status: data?.status,
        assignedTo: data?.assigned_to,
        assignedAt: data?.assigned_at,
        resolvedAt: data?.resolved_at,
        closedAt: data?.closed_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        user: data?.user_profiles ? {
          id: data?.user_profiles?.id,
          fullName: data?.user_profiles?.full_name,
          email: data?.user_profiles?.email,
          avatarUrl: data?.user_profiles?.avatar_url
        } : null,
        assignedUser: data?.assigned_user ? {
          id: data?.assigned_user?.id,
          fullName: data?.assigned_user?.full_name,
          email: data?.assigned_user?.email
        } : null
      };
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  },

  /**
   * Update ticket status
   */
  async updateTicketStatus(ticketId, status) {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.update({ status })?.eq('id', ticketId)?.select(`
          *,
          user_profiles!support_tickets_user_id_fkey (
            id,
            full_name,
            email
          )
        `)?.single();

      if (error) throw error;

      // Trigger notification
      await triggerTicketStatusUpdateNotification(ticketId, {
        subject: data?.subject,
        status: data?.status,
        userId: data?.user_id
      });

      return {
        id: data?.id,
        status: data?.status,
        resolvedAt: data?.resolved_at,
        closedAt: data?.closed_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating ticket status:', error);
      throw error;
    }
  },

  /**
   * Assign ticket to support staff (admin only)
   */
  async assignTicket(ticketId, assignedToId) {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.update({ assigned_to: assignedToId })?.eq('id', ticketId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        assignedTo: data?.assigned_to,
        assignedAt: data?.assigned_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error assigning ticket:', error);
      throw error;
    }
  },

  /**
   * Update ticket priority (admin only)
   */
  async updateTicketPriority(ticketId, priority) {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.update({ priority })?.eq('id', ticketId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        priority: data?.priority,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating ticket priority:', error);
      throw error;
    }
  },

  /**
   * Get ticket messages
   */
  async getTicketMessages(ticketId) {
    try {
      const { data, error } = await supabase?.from('ticket_messages')?.select(`
          *,
          sender:user_profiles!ticket_messages_sender_id_fkey (
            id,
            full_name,
            email,
            avatar_url
          )
        `)?.eq('ticket_id', ticketId)?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(message => ({
        id: message?.id,
        ticketId: message?.ticket_id,
        senderId: message?.sender_id,
        message: message?.message,
        isInternal: message?.is_internal,
        createdAt: message?.created_at,
        sender: message?.sender ? {
          id: message?.sender?.id,
          fullName: message?.sender?.full_name,
          email: message?.sender?.email,
          avatarUrl: message?.sender?.avatar_url
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching ticket messages:', error);
      throw error;
    }
  },

  /**
   * Add message to ticket
   */
  async addTicketMessage(ticketId, message, isInternal = false) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get ticket details for notification
      const { data: ticket } = await supabase?.from('support_tickets')?.select('subject, user_id')?.eq('id', ticketId)?.single();

      const { data, error } = await supabase?.from('ticket_messages')?.insert({
          ticket_id: ticketId,
          sender_id: user?.id,
          message,
          is_internal: isInternal
        })?.select(`
          *,
          sender:user_profiles!ticket_messages_sender_id_fkey (
            id,
            full_name,
            email,
            avatar_url
          )
        `)?.single();

      if (error) throw error;

      // Trigger notification if message is for the user (not internal)
      if (ticket && user?.id !== ticket?.user_id && !isInternal) {
        await triggerTicketMessageNotification(ticketId, {
          subject: ticket?.subject,
          senderName: data?.sender?.full_name || 'Support Team',
          recipientId: ticket?.user_id,
          isInternal
        });
      }

      return {
        id: data?.id,
        ticketId: data?.ticket_id,
        senderId: data?.sender_id,
        message: data?.message,
        isInternal: data?.is_internal,
        createdAt: data?.created_at,
        sender: data?.sender ? {
          id: data?.sender?.id,
          fullName: data?.sender?.full_name,
          email: data?.sender?.email,
          avatarUrl: data?.sender?.avatar_url
        } : null
      };
    } catch (error) {
      console.error('Error adding ticket message:', error);
      throw error;
    }
  },

  /**
   * Get ticket statistics (admin only)
   */
  async getTicketStatistics() {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.select('status, priority, category');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        byStatus: {},
        byPriority: {},
        byCategory: {}
      };

      data?.forEach(ticket => {
        stats.byStatus[ticket.status] = (stats?.byStatus?.[ticket?.status] || 0) + 1;
        stats.byPriority[ticket.priority] = (stats?.byPriority?.[ticket?.priority] || 0) + 1;
        stats.byCategory[ticket.category] = (stats?.byCategory?.[ticket?.category] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching ticket statistics:', error);
      throw error;
    }
  }
};